/**
 * Primary vs overflow actions for the staged-product detail footer while the
 * pipeline still owns the row (`remediating` / `verifying` / `failed`).
 *
 * The ledger's next action is the default click; Approve / Promote stay
 * available in the dropdown so an admin can override when the listing is
 * already good enough.
 */

export type StagedDetailActionId =
  | 'accept_all_ai'
  | 'enrich_ids'
  | 'generate_ai'
  | 'send_to_review'
  | 'approve'
  | 'approve_and_promote'
  | 'reject';

export interface StagedDetailAction {
  id: StagedDetailActionId;
  label: string;
  description?: string;
  color: 'success' | 'primary' | 'info' | 'error';
  variant: 'contained' | 'outlined';
}

export interface StagedDetailActionInput {
  stagingStatus: string;
  existingCanonicalId?: string | null;
  hasPendingAi: boolean;
  canReview: boolean;
  canApprove: boolean;
  canPromote: boolean;
  remediation?: {
    openGaps?: string[];
    gaps?: Record<string, { nextAction?: string | null; exhausted?: boolean }>;
  };
}

/** Statuses where the pipeline is still working and the footer used to hide Approve. */
export const PIPELINE_OWNED_STATUSES = new Set(['remediating', 'verifying', 'failed']);

const SEND_TO_REVIEW_STATUSES = new Set(['pending', 'remediating', 'verifying', 'failed', 'approved']);

function promoteLabel(existingCanonicalId?: string | null): string {
  return existingCanonicalId ? 'Approve & Apply Update' : 'Approve & Promote';
}

/**
 * First open, non-exhausted gap whose nextAction has a one-click UI handler.
 */
export function nextRunnableRemediationAction(
  input: Pick<StagedDetailActionInput, 'remediation' | 'hasPendingAi'>,
): StagedDetailActionId | null {
  const open = input.remediation?.openGaps || [];
  const gaps = input.remediation?.gaps || {};
  for (const gapId of open) {
    const st = gaps[gapId];
    if (!st || st.exhausted) continue;
    const next = st.nextAction;
    if (next === 'ai_accept' && input.hasPendingAi) return 'accept_all_ai';
    if (next === 'identifier_enrichment') return 'enrich_ids';
    if (
      next === 'ai_generate_short' ||
      next === 'ai_generate_description' ||
      next === 'ai_generate_features'
    ) {
      return 'generate_ai';
    }
  }
  return null;
}

function defineAction(id: StagedDetailActionId, input: StagedDetailActionInput): StagedDetailAction {
  switch (id) {
    case 'accept_all_ai':
      return {
        id,
        label: 'Accept All AI Suggestions',
        description: 'Copy AI drafts into the catalog fields (recommended next step)',
        color: 'success',
        variant: 'contained',
      };
    case 'enrich_ids':
      return {
        id,
        label: 'Enrich IDs',
        description: 'Look up UPC / EAN / MPN (recommended next step)',
        color: 'primary',
        variant: 'contained',
      };
    case 'generate_ai':
      return {
        id,
        label: 'Generate AI Description',
        description: 'Fill missing description / features (recommended next step)',
        color: 'primary',
        variant: 'contained',
      };
    case 'send_to_review':
      return {
        id,
        label: 'Send to review',
        description: 'Pull this product out of the pipeline for a human',
        color: 'info',
        variant: 'contained',
      };
    case 'approve':
      return {
        id,
        label: 'Approve',
        description: 'Skip remaining remediation and mark approved',
        color: 'success',
        variant: 'outlined',
      };
    case 'approve_and_promote':
      return {
        id,
        label: promoteLabel(input.existingCanonicalId),
        description: 'Skip remaining remediation and write to the catalog',
        color: 'success',
        variant: 'outlined',
      };
    case 'reject':
      return {
        id,
        label: 'Reject',
        description: 'Remove this product from the queue',
        color: 'error',
        variant: 'outlined',
      };
  }
}

export function pickPipelineOwnedDetailActions(input: StagedDetailActionInput): {
  primary: StagedDetailAction | null;
  overflow: StagedDetailAction[];
} {
  if (!PIPELINE_OWNED_STATUSES.has(input.stagingStatus)) {
    return { primary: null, overflow: [] };
  }

  const recommended = nextRunnableRemediationAction(input);
  let primaryId: StagedDetailActionId | null = recommended;
  if (!primaryId && input.canReview && SEND_TO_REVIEW_STATUSES.has(input.stagingStatus)) {
    primaryId = 'send_to_review';
  }

  const overflowIds: StagedDetailActionId[] = [];
  const push = (id: StagedDetailActionId) => {
    if (id !== primaryId && !overflowIds.includes(id)) overflowIds.push(id);
  };

  if (input.hasPendingAi) push('accept_all_ai');
  if (input.canReview && SEND_TO_REVIEW_STATUSES.has(input.stagingStatus)) push('send_to_review');
  if (input.canApprove) {
    push('approve');
    if (input.canPromote) push('approve_and_promote');
    push('reject');
  }

  // The primary slot must never be empty while overrides exist: the split
  // control renders its dropdown off the primary button, so a null primary
  // with 2+ overflow actions would leave all but the first unreachable
  // (e.g. content_approve without content_review and no runnable next step).
  if (!primaryId && overflowIds.length > 0) {
    primaryId = overflowIds.shift()!;
  }

  return {
    primary: primaryId ? defineAction(primaryId, input) : null,
    overflow: overflowIds.map((id) => defineAction(id, input)),
  };
}
